import type { ItemTabelaPreco, PassoProduto, ProdutoCatalogo } from '../api';

/**
 * Preço "de saída" de um produto para exibição no cardápio e nas propagandas do
 * tablet. Considera os PASSOS: se o produto principal tem valor baixo (ou zero)
 * mas passos obrigatórios, mostrar só `produto.valor` engana o cliente. Aqui
 * calculamos o PISO (o mínimo que ele vai pagar) e um rótulo "a partir de"
 * quando o total ainda pode subir.
 *
 * A MESMA regra roda em `backend/src/lib/preco-base.ts`, para a integração
 * externa. Mudou aqui, muda lá: divergir faz o bot anunciar outro preço.
 */
export interface PrecoBase {
  /** 'a partir de' quando o total pode variar; '' quando o preço é fixo. */
  label: 'a partir de' | '';
  /** Piso: produto.valor + contribuição mínima dos passos obrigatórios/auto. */
  valor: number;
  valorOriginal: number | null;
}

/**
 * Contribuição MÍNIMA de um passo ao piso do produto:
 *  - itens auto-adicionados (`adicionarAutomaticamente`) sempre entram (é o que
 *    o cliente paga por padrão);
 *  - se ainda não atingiu `quantidadeMinima`, completa com a opção mais barata, repetida;
 *  - aplica o `tipoCalculo` sobre essa seleção mínima (espelha o
 *    `calcularValorPasso` da página do produto).
 */
function contribuicaoMinimaPasso(passo: PassoProduto, usarOriginal = false): number {
  const precoDe = (it: ItemTabelaPreco): number =>
    usarOriginal ? (it.valorOriginal ?? it.valor) : it.valor;
  const itens = passo.tabelaPreco?.itens ?? [];
  if (itens.length === 0) return 0;

  // Seleção mínima: id do item -> quantidade.
  const sel = new Map<number, number>();
  let qtdTotal = 0;

  // 1) Itens já adicionados por padrão.
  for (const it of itens) {
    if (it.adicionarAutomaticamente) {
      const q = it.quantidadeAutomatica ?? 1;
      sel.set(it.id, (sel.get(it.id) ?? 0) + q);
      qtdTotal += q;
    }
  }

  // 2) Completa até o mínimo obrigatório com a opção mais barata, repetida.
  const min = passo.quantidadeMinima ?? 0;
  if (qtdTotal < min) {
    // Ordena pelo preço DESTA passada: com desconto a opção mais barata pode ser outra.
    const maisBaratos = [...itens].sort((a, b) => precoDe(a) - precoDe(b));
    while (qtdTotal < min) {
      const it = maisBaratos[0]
      if (!it) break;
      sel.set(it.id, (sel.get(it.id) ?? 0) + 1);
      qtdTotal += 1;
    }
  }

  if (qtdTotal === 0) return 0;

  const escolhidos = itens.filter((it) => (sel.get(it.id) ?? 0) > 0);
  // Só com dado torto. Sem isto, MAIOR_VALOR dá -Infinity e FRACAO dá NaN.
  if (escolhidos.length === 0) return 0;

  if (passo.tipoCalculo === 'MAIOR_VALOR') {
    return Math.max(...escolhidos.map(precoDe));
  }
  if (passo.tipoCalculo === 'FRACAO') {
    return escolhidos.reduce((s, it) => s + precoDe(it), 0) / escolhidos.length;
  }
  // UNITARIO
  return escolhidos.reduce((s, it) => s + precoDe(it) * (sel.get(it.id) ?? 0), 0);
}

/** O total pode subir acima do piso por causa deste passo? */
function passoPodeAumentar(passo: PassoProduto): boolean {
  const itens = passo.tabelaPreco?.itens ?? [];
  const temOpcaoPaga = itens.some((it: ItemTabelaPreco) => it.valor > 0);
  if (!temOpcaoPaga) return false;

  const min = passo.quantidadeMinima ?? 0;
  if (min <= 0) return true; // opcional com opção paga: cliente pode adicionar

  // Obrigatório: sobe se dá pra escolher mais que o mínimo ou se há preços diferentes.
  const precosDistintos = new Set(itens.map((it) => it.valor)).size > 1;
  const podeEscolherMais = (passo.quantidadeMaxima ?? 0) > min;
  return precosDistintos || podeEscolherMais;
}

/**
 * Calcula o preço de exibição (piso + rótulo) de um produto do catálogo.
 *
 * Exemplos:
 *  - Produto simples R$10, sem passos            -> { label: '',           valor: 10 }
 *  - Hambúrguer R$20 + refri R$5 (opcional)      -> { label: 'a partir de', valor: 20 }
 *  - Produto R$0 + passo obrigatório (min R$15)  -> { label: 'a partir de', valor: 15 }
 */
export function precoBaseProduto(produto: ProdutoCatalogo): PrecoBase {
  let valor = produto.valor;
  let valorOriginal = produto.valorOriginal ?? produto.valor;
  let variavel = false;
  let temPrecoPorHorario = produto.precoPorHorarioAtivo === true;

  for (const passo of produto.passos ?? []) {
    valor += contribuicaoMinimaPasso(passo);
    valorOriginal += contribuicaoMinimaPasso(passo, true);
    if (passoPodeAumentar(passo)) variavel = true;
    if (passo.tabelaPreco?.itens.some((item) => item.precoPorHorarioAtivo)) temPrecoPorHorario = true;
  }

  return {
    label: variavel ? 'a partir de' : '',
    valor,
    valorOriginal: temPrecoPorHorario && valorOriginal > valor ? valorOriginal : null,
  };
}
